import { useEffect, useState } from 'react';
import { Cookie } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { initGA, trackPageView, setAnalyticsConsent, getStoredConsent } from '@/utils/ga';

// GDPR/CCPA cookie consent banner. Blocks analytics until the visitor
// makes a choice; the choice persists in localStorage and can be reopened
// via the "Cookie Preferences" footer link (mementa:open-cookie-prefs event).
export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(true);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) setVisible(true);
    const reopen = () => {
      const cur = getStoredConsent();
      setAnalyticsOn(cur ? !!cur.analytics : true);
      setCustomizing(true);
      setVisible(true);
    };
    window.addEventListener('mementa:open-cookie-prefs', reopen);
    return () => window.removeEventListener('mementa:open-cookie-prefs', reopen);
  }, []);

  const save = (analytics) => {
    setAnalyticsConsent(analytics);
    if (analytics) {
      initGA();
      trackPageView(window.location.pathname + window.location.search);
    }
    setVisible(false);
    setCustomizing(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-[60] p-3 sm:p-4"
      role="dialog"
      aria-label="Cookie consent"
    >
      <div
        className="max-w-3xl mx-auto rounded-2xl shadow-2xl border p-5"
        style={{ background: '#FFFAF5', borderColor: '#E8D5C4' }}
      >
        <div className="flex items-start gap-3">
          <Cookie className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#9E6752' }} />
          <div className="flex-1">
            <p className="text-sm leading-relaxed" style={{ color: '#20212B' }}>
              <strong>We value your privacy.</strong> Mementa uses one essential cookie to keep you
              signed in, and, with your consent, Google Analytics cookies to understand how the site
              is used. When you click a product link, our affiliate partners (like Rakuten
              Advertising) may set their own tracking cookies. See our{' '}
              <a href="/cookies" className="underline" style={{ color: '#9E6752' }}>Cookie Policy</a>{' '}
              and{' '}
              <a href="/privacy" className="underline" style={{ color: '#9E6752' }}>Privacy Policy</a>.
            </p>

            {customizing && (
              <div className="mt-4 space-y-3 rounded-xl border p-4" style={{ borderColor: '#E8D5C4', background: 'white' }}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: '#20212B' }}>Essential cookies</p>
                    <p className="text-xs" style={{ color: '#73766A' }}>Sign-in session and security. Always on.</p>
                  </div>
                  <Switch checked disabled data-testid="cookie-essential-toggle" />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: '#20212B' }}>Analytics cookies</p>
                    <p className="text-xs" style={{ color: '#73766A' }}>Google Analytics, helps us improve the site.</p>
                  </div>
                  <Switch checked={analyticsOn} onCheckedChange={setAnalyticsOn} data-testid="cookie-analytics-toggle" />
                </div>
                <p className="text-xs" style={{ color: '#73766A' }}>
                  Affiliate tracking cookies are set by merchants and networks on their own sites
                  after you click a product link; manage those in your browser or via the opt-out
                  links in our <a href="/cookies" className="underline" style={{ color: '#9E6752' }}>Cookie Policy</a>.
                </p>
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {customizing ? (
                <button
                  data-testid="cookie-save-prefs-btn"
                  onClick={() => save(analyticsOn)}
                  className="px-5 py-2 rounded-full text-sm font-semibold text-white transition-transform hover:scale-105"
                  style={{ background: '#9E6752' }}
                >
                  Save preferences
                </button>
              ) : (
                <>
                  <button
                    data-testid="cookie-accept-all-btn"
                    onClick={() => save(true)}
                    className="px-5 py-2 rounded-full text-sm font-semibold text-white transition-transform hover:scale-105"
                    style={{ background: '#9E6752' }}
                  >
                    Accept all
                  </button>
                  <button
                    data-testid="cookie-reject-btn"
                    onClick={() => save(false)}
                    className="px-5 py-2 rounded-full text-sm font-semibold border transition-colors hover:bg-gray-50"
                    style={{ color: '#20212B', borderColor: '#C9CBCE', background: 'white' }}
                  >
                    Reject non-essential
                  </button>
                  <button
                    data-testid="cookie-customize-btn"
                    onClick={() => setCustomizing(true)}
                    className="px-4 py-2 text-sm font-medium underline"
                    style={{ color: '#9E6752' }}
                  >
                    Customize
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
